import { useEffect, useRef, useState } from 'react';
import { useStore, useStoreMap } from 'effector-react';
import { type XYCoord, useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import type { ECategoryItem } from '@types';
import { $categoryMap, $categoryMapApi } from '@stores/dataStores';
import cn from '@utils/cn';
import {
  dragScenesToCategory,
  dragSubCategory,
} from '@modules/category/actions/dragActions';
import { offset, size, useFloating } from '@floating-ui/react-dom';
import { sortSubCategoryScenes } from '@modules/category/actions/sortScenesActions';
import { deleteSubCategory } from '@modules/category/actions/deleteActions';
import { showConfirmModal } from '@components/ConfirmModal';
import { showInputModal } from '@components/InputModal';
import { showScenesCopyModal } from '@modules/category/ScenesCopyModal';
import { MoreMenu } from '../MoreMenu';
import {
  $isSubCategoryDragging,
  $scenesManagement,
  $scenesManagementApi,
} from '../stores';
import {
  SMDndType,
  type SMDragItem,
  isScenesItem,
  isSubCategoryItem,
} from '../dnd';

interface Props {
  id: number;
  parentId: number;
}

export function SubItem({ id, parentId }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { name, sceneIds } = useStoreMap({
    store: $categoryMap,
    keys: [id],
    fn: (state, [_id]) => state.get(_id) || ({} as ECategoryItem),
  });
  const isActive = useStoreMap({
    store: $scenesManagement,
    keys: [id],
    fn: (state, [_id]) => state.categoryPath[1] === _id && !state.query,
  });
  const [hoverPos, setHoverPos] = useState<null | 'top' | 'bottom'>(null);
  const isSubCategoryDragging = useStore($isSubCategoryDragging);

  const { x, y, reference, floating, strategy } = useFloating({
    placement: hoverPos === 'top' ? 'top' : 'bottom',
    strategy: 'fixed',
    middleware: [
      offset(hoverPos === 'top' ? -4 : 0),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  useEffect(() => {
    if (isActive) {
      $scenesManagementApi.update((draft) => {
        draft.sceneList = sceneIds;
      });
    }
  }, [isActive, sceneIds]);

  const [{ isDragging }, drag, preview] = useDrag({
    type: SMDndType.subCategory,
    item: { id, parentId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, []);

  const [{ isOver, isSubCategoryOver }, drop] = useDrop({
    accept: [SMDndType.scenes, SMDndType.subCategory],
    collect: (monitor) => {
      return {
        isOver: monitor.isOver() && monitor.getItemType() === SMDndType.scenes,
        isSubCategoryOver:
          monitor.isOver() && monitor.getItemType() === SMDndType.subCategory,
      };
    },
    hover: (item: SMDragItem, monitor) => {
      if (!ref.current) return;
      const type = monitor.getItemType();
      if (isSubCategoryItem(item, type)) {
        const rect = ref.current.getBoundingClientRect();
        const middleY = (rect.bottom - rect.top) / 2;
        const clientOffset = monitor.getClientOffset() as XYCoord;
        const hoverY = clientOffset.y - rect.top;
        setHoverPos(hoverY < middleY ? 'top' : 'bottom');
      }
    },
    drop: (item: SMDragItem, monitor) => {
      const type = monitor.getItemType();
      if (isScenesItem(item, type)) {
        if (item.includeSelected) {
          $scenesManagementApi.update((draft) => {
            draft.selected = [];
          });
        }
        dragScenesToCategory({
          categoryId: id,
          ids: item.ids,
        });
      } else if (isSubCategoryItem(item, type)) {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const middleY = (rect.bottom - rect.top) / 2;
        const clientOffset = monitor.getClientOffset() as XYCoord;
        const hoverY = clientOffset.y - rect.top;
        dragSubCategory({
          id: item.id,
          parentId: item.parentId,
          targetId: id,
          targetParentId: parentId,
          pos: hoverY < middleY ? 'top' : 'bottom',
        });
      }
    },
  });

  drop(drag(ref));

  const handleDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '确认删除分组',
      content: '分组下所有场景也都将会被删除，确认删除？',
    });
    if (!confirmed) return;
    deleteSubCategory({ subId: id, parentId });
    $scenesManagementApi.update((draft) => {
      if (draft.categoryPath[1] !== id) return;
      const map = $categoryMap.getState();
      const list = map.get(parentId)?.categoryIds || [];
      const nextId = list.filter((_id) => _id !== id)[0];
      if (nextId) {
        draft.categoryPath = [parentId, nextId];
        draft.sceneList = $categoryMap.getState().get(nextId)?.sceneIds || [];
      } else {
        draft.categoryPath = [parentId];
        draft.sceneList = sceneIds;
      }
      draft.selected = [];
    });
  };

  const handleRename = async () => {
    const { value } = await showInputModal({
      title: '分组重命名',
      placeholder: '请输入分组名称',
      initialValue: name,
      maxLength: 50,
    });
    if (value) {
      $categoryMapApi.update((draft) => {
        draft.get(id)!.name = value;
      });
    }
  };

  return (
    <div ref={ref}>
      <div
        ref={reference}
        className={cn(
          'color-[#909090] relative flex h-40 items-center bg-[#f8f8f8] pl-45 pr-10',
          {
            'text-blue': isActive,
            'bg-[#f8f8f8]': !isOver,
            'bg-[#efefef]': isOver,
            'invisible h-0': isDragging,
            'hover:bg-[#efefef]': !isSubCategoryDragging && !isOver,
          }
        )}
        onClick={(e) => {
          e.stopPropagation();
          if (isActive) return;
          $scenesManagementApi.update((draft) => {
            draft.categoryPath = [parentId, id];
            draft.sceneList = sceneIds;
            draft.selected = [];
            draft.query = '';
          });
        }}
      >
        <div title={name} className="grow truncate">
          {name}
        </div>
        <MoreMenu
          className="ml-auto"
          items={['重新命名', '删除分组', '一键排序', '跨作品复制'] as const}
          onSelect={async (val) => {
            if (val === '重新命名') {
              await handleRename();
            } else if (val === '删除分组') {
              await handleDelete();
            } else if (val === '一键排序') {
              sortSubCategoryScenes(id);
            } else if (val === '跨作品复制') {
              showScenesCopyModal({ level: '二级分组', subCategoryId: id });
            }
          }}
        />
      </div>
      {isSubCategoryOver && (
        <div
          ref={floating}
          style={{
            position: strategy,
            top: y ?? '',
            left: x ?? '',
          }}
          className="pointer-events-none z-20 h-4 bg-green"
        />
      )}
    </div>
  );
}

export default SubItem;
